/**
 * NO03 - Fakturakopi List Component
 * Displays search results in a table with infinite scrolling
 */

import { useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Description as DescriptionIcon } from '@mui/icons-material';
import { makeStyles } from 'tss-react/mui';
import { AppState } from '../../../store/rootReducer';
import NO03TableHead from './NO03TableHead';
import NO03 from '../../../models/NO03';
import * as APICalls from '../../../felles/APICalls_NO03';
import * as actionsNO03 from '../../../store/actions/indexNO03';

const useStyles = makeStyles()((theme) => ({
  container: {
    margin: theme.spacing(2),
    maxHeight: 'calc(100vh - 400px)',
  },
  headerSection: {
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #ddd',
  },
  resultSummary: {
    fontWeight: 500,
  },
  emptyState: {
    padding: theme.spacing(8),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  loadingContainer: {
    padding: theme.spacing(8),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  tableRow: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  amountNegative: {
    color: theme.palette.error.main,
  },
  amountPositive: {
    color: theme.palette.success.main,
  },
  loadMoreContainer: {
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

export default function NO03List() {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const invoices = useSelector((state: AppState) => state.NO03.invoices);
  const totalAmount = useSelector((state: AppState) => state.NO03.totalAmount);
  const loading = useSelector((state: AppState) => state.NO03.loading);
  const loadingMore = useSelector((state: AppState) => state.NO03.loadingMore);
  const hasMore = useSelector((state: AppState) => state.NO03.hasMore);
  const currentPage = useSelector((state: AppState) => state.NO03.currentPage);
  const lastSearchParams = useSelector((state: AppState) => state.NO03.lastSearchParams);
  const error = useSelector((state: AppState) => state.NO03.error);

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('nb-NO', {
      style: 'currency',
      currency: 'NOK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('nb-NO');
  };

  const handleViewInvoice = async (fakturanr: string) => {
    try {
      const response = await APICalls.getInvoicePDF(fakturanr);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error('Failed to load invoice PDF:', error);
      alert('Kunne ikke laste faktura PDF. Vennligst prøv igjen.');
    }
  };

  // Infinite scroll detection
  useEffect(() => {
    const handleScroll = () => {
      const container = tableContainerRef.current;
      if (!container || loadingMore || !hasMore || !lastSearchParams) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      const scrollThreshold = 50;

      if (scrollHeight - scrollTop - clientHeight < scrollThreshold) {
        const nextPage = currentPage + 1;
        const paginatedParams = {
          ...lastSearchParams,
          page: nextPage,
          pageSize: 100,
        };
        dispatch(actionsNO03.NO03LoadMoreRequest(paginatedParams));
      }
    };

    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [loadingMore, hasMore, currentPage, lastSearchParams, dispatch]);

  if (loading) {
    return (
      <Paper className={classes.container}>
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper className={classes.container}>
        <Box className={classes.emptyState}>
          <Typography color="error">{error}</Typography>
        </Box>
      </Paper>
    );
  }

  if (invoices.length === 0) {
    return (
      <Paper className={classes.container}>
        <Box className={classes.emptyState}>
          <Typography variant="h6">Ingen fakturaer funnet</Typography>
          <Typography variant="body2">
            Søk etter fakturaer ved å fylle ut søkeskjemaet ovenfor
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper className={classes.container}>
      {/* Results Summary */}
      <Box className={classes.headerSection}>
        <Typography variant="h6" className={classes.resultSummary}>
          Fakturaer
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {invoices.length} fakturaer — totalt {formatCurrency(totalAmount)}
        </Typography>
      </Box>

      {/* Results Table */}
      <TableContainer ref={tableContainerRef}>
        <Table stickyHeader>
          <NO03TableHead />
          <TableBody>
            {invoices.map((invoice: NO03, index: number) => (
              <TableRow key={`${invoice.fakturanr}-${index}`} className={classes.tableRow}>
                <TableCell>{invoice.kundenr}</TableCell>
                <TableCell>{invoice.kundenavn}</TableCell>
                <TableCell>{invoice.fakturanr}</TableCell>
                <TableCell>{invoice.fakturaKid}</TableCell>
                <TableCell>{invoice.ordre}</TableCell>
                <TableCell>{formatDate(invoice.fakturadato)}</TableCell>
                <TableCell
                  align="right"
                  className={
                    invoice.fakturatotal < 0
                      ? classes.amountNegative
                      : classes.amountPositive
                  }
                >
                  {formatCurrency(invoice.fakturatotal)}
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Vis faktura">
                    <IconButton
                      size="small"
                      onClick={() => handleViewInvoice(invoice.fakturanr)}
                    >
                      <DescriptionIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Loading More Indicator */}
      {loadingMore && (
        <Box className={classes.loadMoreContainer}>
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Laster flere fakturaer...
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
